import { navigate } from 'gatsby';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import styled from 'styled-components';

import BaseCard from '@ui/atoms/BaseCard';
import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import Eyebrow from '@ui/atoms/Eyebrow';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import FlexRow from '@ui/atoms/FlexRow';
import Pill from '@ui/atoms/Pill';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import {
	IMemberInvoiceProposal,
	IMemberInvoiceProposalLineItem,
} from '@hooks/useMemberInvoiceProposal';

import { centsToDollars } from '@utils/money';

import ExclamationQuotes from '@assets/vectors/exclamation-quotes.inline.svg';

import theme from '@theme/FolxTheme';

interface IMedicationBillCardProps {
	bill: IMemberInvoiceProposal;
	paidBillLabel: string;
	paidBillDisclaimer: string;
	unpaidBillShipping: string;
	unpaidBillDisclaimer: string;
	expiredBillDisclaimer: string;
}

interface IMedicationBillCardPaidProps {
	paidBillLabel?: string;
	paidBillDisclaimer?: string;
	bill?: IMemberInvoiceProposal;
}

interface IMedicationBillCardUnpaidProps {
	bill: IMemberInvoiceProposal;
	unpaidBillShipping: string;
	unpaidBillDisclaimer: string;
}

interface IMedicationBillCardExpiredProps {
	bill: IMemberInvoiceProposal;
	expiredBillDisclaimer: string;
}

const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const SpacedColumn = spaced<IFlexColumn>(FullWidthColumn);

const StatusPillContainer = styled.div`
	top: ${theme.spacing.sm};
	right: ${theme.spacing.sm};
	display: flex;
	justify-content: end;
	position: absolute;
	width: 100%;
`;

const StatusPill = styled(Pill)`
	min-width: 10rem;
	padding: ${theme.spacing.xs} ${theme.spacing.smmd};
	width: auto;
`;

const InfoDisplayCard = styled(FlexColumn).attrs({
	breakpoint: 'tablet',
})`
	position: relative;
	padding: ${theme.spacing.md};
	row-gap: ${theme.spacing.lg};
	width: 100%;
`;

const LineItemCard = styled(BaseCard)`
	padding: 10px;
`;

const DisclaimerCard = styled(BaseCard)`
	padding: 20px;
`;

const LineItemRows = styled(FlexRow)`
	justify-content: space-between;
`;

const BillTotalRow = styled(FlexRow)`
	justify-content: end;
`;

const ExclamationQuotesContainer = styled.div`
	display: inline-block;
	vertical-align: middle;
	margin-right: 3px;
`;

const BasicTextRight = styled(BasicText)`
	text-align: right;
	text-transform: capitalize;
`;

const LongformTextRight = styled(BasicText)`
	text-align: right;
`;

const formatDate = (date?: Date) =>
	date ? DateTime.fromJSDate(new Date(date)).toLocaleString() : '';

const getMedicationInfo = (originalDescription: string) => {
	const name =
		originalDescription &&
		originalDescription.split(/[0-9]/).length > 1 &&
		originalDescription.split(/[0-9]/)[0]
			? originalDescription.split(/[0-9]/)[0]
			: originalDescription;

	const description =
		originalDescription &&
		originalDescription.split(/[0-9]/).length > 1 &&
		originalDescription.substring(
			originalDescription.split(/[0-9]/)[0].length,
			originalDescription.length
		)
			? originalDescription.substring(
					originalDescription.split(/[0-9]/)[0].length,
					originalDescription.length
			  )
			: originalDescription;

	return { name, description };
};

const NewBillCardPaid = ({ bill }: IMedicationBillCardPaidProps) => {
	console.log('paid bill: ', bill);
	const total = centsToDollars(bill?.invoice?.total ?? 0);
	const paidDate = formatDate(bill?.invoice?.created);
	const amountPaid = centsToDollars(bill?.invoice?.amountPaid ?? 0);
	const balance = centsToDollars(bill?.invoice?.amountRemaining ?? 0);

	// second date info & bill topics are key-value pairs to enhance flexibility
	const secondDateInfo = {
		key: 'Created',
		value: formatDate(bill?.creationDate),
	};
	const billTopics = useMemo(
		() =>
			bill?.lineItems.map((item) => {
				const { name, description } = getMedicationInfo(item.description);
				return [
					{ key: 'Medication', value: name },
					{ key: 'Description', value: description },
					{ key: 'Instructions', value: item.instructions },
					{ key: 'Quantity', value: item.quantity },
					{ key: '' },
					// TODO: removed, not mvp
					// { key: 'Cost', value: centsToDollars(item.price ?? 0) },
				];
			}) ?? [],
		[bill?.lineItems]
	);

	const billedTo = bill?.invoice?.paymentMethod?.card
		? `Billed to ${bill.invoice.paymentMethod.card.brand}...${bill.invoice.paymentMethod.card.last4}`
		: undefined;

	return (
		<InfoDisplayCard>
			<SpacedColumn gap="sm">
				<StatusPillContainer>
					<StatusPill color="green" variant="fineprintBold" title="Paid" />
				</StatusPillContainer>
				<BasicText variant="field">{total}</BasicText>
				{billedTo && <BasicText variant="fineprint">{billedTo}</BasicText>}
				<Eyebrow size="xs" underline={true} weight="normal" color="placeholderGray"></Eyebrow>
				<SpacedColumn gap="lg">
					<SpacedColumn gap="sm">
						<LineItemRows>
							<BasicText variant="fineprintBold">Paid</BasicText>
							<BasicText variant="fineprint">{paidDate}</BasicText>
						</LineItemRows>
						<LineItemRows>
							<BasicText variant="fineprintBold">{secondDateInfo.key}</BasicText>
							<BasicText variant="fineprint">{secondDateInfo.value}</BasicText>
						</LineItemRows>
					</SpacedColumn>
					{billTopics.map((topic, index) => (
						<BillLineItem topics={topic} key={index} />
					))}
					<SpacedColumn gap="sm">
						<LineItemRows>
							<BasicText variant="fineprintBold">Paid</BasicText>
							<BasicText variant="fineprint">{amountPaid}</BasicText>
						</LineItemRows>
						<BillTotalRow gap="md">
							<BasicText variant="fineprintBold">Balance</BasicText>
							<BasicText variant="fineprint">{balance}</BasicText>
						</BillTotalRow>
					</SpacedColumn>
				</SpacedColumn>
			</SpacedColumn>
		</InfoDisplayCard>
	);
};

type BillLineItemProps = {
	topics: { key: string; value: string }[];
};
const BillLineItem = ({ topics }: BillLineItemProps) => (
	<LineItemCard bgColor="lightBeige" borderless>
		<SpacedColumn gap="sm">
			{topics.map((topic) => (
				<LineItemRows gap="sm" key={topic.key}>
					<BasicText variant="fineprintBold">{topic.key}</BasicText>
					<BasicTextRight variant="fineprint">{topic.value}</BasicTextRight>
				</LineItemRows>
			))}
		</SpacedColumn>
	</LineItemCard>
);

// TODO check if it's adequate to replace bill.lineItems with BillLineItem
const BillCardUnpaid = ({
	bill,
	unpaidBillShipping,
	unpaidBillDisclaimer,
}: IMedicationBillCardUnpaidProps) => {
	const total = bill.total ? (bill.total / 100).toFixed(2) : '';
	const dueDate = bill.dueDate ? DateTime.fromJSDate(new Date(bill.dueDate)).toLocaleString() : '';
	const creationDate = bill.creationDate
		? DateTime.fromJSDate(new Date(bill.creationDate)).toLocaleString()
		: '';
	return (
		<>
			<StatusPillContainer>
				<StatusPill color="darkBeige" variant="fineprintBold" title="Unpaid" />
			</StatusPillContainer>
			<BasicText variant="field">{`$${total}`}</BasicText>
			<Eyebrow size="xs" underline={true} weight="normal" color="placeholderGray"></Eyebrow>
			<SpacedColumn gap="lg">
				<SpacedColumn gap="sm">
					<LineItemRows>
						<BasicText variant="fineprintBold">Due</BasicText>
						<BasicText variant="fineprint">{dueDate}</BasicText>
					</LineItemRows>
					<LineItemRows>
						<BasicText variant="fineprintBold">Created</BasicText>
						<BasicText variant="fineprint">{creationDate}</BasicText>
					</LineItemRows>
				</SpacedColumn>
				{bill.lineItems &&
					bill.lineItems.map((lineItem: IMemberInvoiceProposalLineItem, index) => (
						<LineItemCard key={`bill-line-item-${index}`} bgColor="lightBeige" borderless>
							<SpacedColumn gap="sm">
								<LineItemRows gap="sm">
									<BasicText variant="fineprintBold">Medication</BasicText>
									<BasicTextRight variant="fineprint">
										{lineItem.description &&
										lineItem.description.split(/[0-9]/).length > 1 &&
										lineItem.description.split(/[0-9]/)[0]
											? lineItem.description.split(/[0-9]/)[0]
											: lineItem.description}
									</BasicTextRight>
								</LineItemRows>
								<LineItemRows gap="sm">
									<BasicText variant="fineprintBold">Description</BasicText>
									<BasicTextRight variant="fineprint">
										{lineItem.description &&
										lineItem.description.split(/[0-9]/).length > 1 &&
										lineItem.description.substring(
											lineItem.description.split(/[0-9]/)[0].length,
											lineItem.description.length
										)
											? lineItem.description.substring(
													lineItem.description.split(/[0-9]/)[0].length,
													lineItem.description.length
											  )
											: lineItem.description}
									</BasicTextRight>
								</LineItemRows>
								{lineItem.instructions && (
									<LineItemRows gap="sm">
										<BasicText variant="fineprintBold">Instructions</BasicText>
										<LongformTextRight variant="fineprint">
											{lineItem.instructions}
										</LongformTextRight>
									</LineItemRows>
								)}
								{lineItem.quantity && (
									<LineItemRows gap="sm">
										<BasicText variant="fineprintBold">Quantity</BasicText>
										<BasicTextRight variant="fineprint">{lineItem.quantity}</BasicTextRight>
									</LineItemRows>
								)}
								<LineItemRows gap="sm">
									<BasicText variant="fineprintBold">Cost</BasicText>
									<BasicTextRight variant="fineprint">
										{`$${lineItem.price ? (lineItem.price / 100).toFixed(2) : 'N/A'}`}
									</BasicTextRight>
								</LineItemRows>
							</SpacedColumn>
						</LineItemCard>
					))}
				<BillTotalRow gap="md">
					<BasicText variant="fineprintBold">Total</BasicText>
					<BasicText variant="fineprint">{`$${total}`}</BasicText>
				</BillTotalRow>
				<Button
					variant="secondary"
					label="Pay now"
					onClick={() => {
						navigate(`/app/med_payment/${bill?.id}`);
					}}
				/>
				<DisclaimerCard bgColor="cardBeige" radius="lg" borderless>
					<SpacedColumn gap="sm">
						<BasicText variant="fineprint">
							<ExclamationQuotesContainer>
								<ExclamationQuotes />
							</ExclamationQuotesContainer>
							{unpaidBillShipping}
						</BasicText>
						<BasicText variant="fineprint">{unpaidBillDisclaimer}</BasicText>
					</SpacedColumn>
				</DisclaimerCard>
			</SpacedColumn>
		</>
	);
};

const BillCardExpired = ({ bill, expiredBillDisclaimer }: IMedicationBillCardExpiredProps) => {
	const total = bill.total ? (bill.total / 100).toFixed(2) : '';
	const creationDate = bill.creationDate
		? DateTime.fromJSDate(new Date(bill.creationDate)).toLocaleString()
		: '';
	return (
		<>
			<StatusPillContainer>
				<StatusPill color="lightRed" variant="fineprintBold" title="Expired" />
			</StatusPillContainer>
			<BasicText variant="field">{`$${total}`}</BasicText>
			<Eyebrow size="xs" underline={true} weight="normal" color="placeholderGray"></Eyebrow>
			<SpacedColumn gap="lg">
				<SpacedColumn gap="sm">
					<LineItemRows>
						<BasicText variant="fineprintBold">Created</BasicText>
						<BasicText variant="fineprint">{creationDate}</BasicText>
					</LineItemRows>
				</SpacedColumn>
				{bill.lineItems &&
					bill.lineItems.map((lineItem: IMemberInvoiceProposalLineItem, index) => (
						<LineItemCard key={`bill-line-item-${index}`} bgColor="lightBeige" borderless>
							<SpacedColumn gap="sm">
								<LineItemRows gap="sm">
									<BasicText variant="fineprintBold">Medication</BasicText>
									<BasicTextRight variant="fineprint">
										{lineItem.description && lineItem.description.split(/[0-9]/)[0]
											? lineItem.description.split(/[0-9]/)[0]
											: lineItem.description}
									</BasicTextRight>
								</LineItemRows>
								{lineItem.instructions && (
									<LineItemRows gap="sm">
										<BasicText variant="fineprintBold">Instructions</BasicText>
										<LongformTextRight variant="fineprint">
											{lineItem.instructions}
										</LongformTextRight>
									</LineItemRows>
								)}
								{lineItem.quantity && (
									<LineItemRows gap="sm">
										<BasicText variant="fineprintBold">Quantity</BasicText>
										<BasicTextRight variant="fineprint">{lineItem.quantity}</BasicTextRight>
									</LineItemRows>
								)}
								{/* <LineItemRows gap="sm">
									<BasicText variant="fineprintBold">Description</BasicText>
									<BasicTextRight variant="fineprint">
										{lineItem.description &&
										lineItem.description.split(/[0-9]/).length > 1 &&
										lineItem.description.substring(
											lineItem.description.split(/[0-9]/)[0].length,
											lineItem.description.length
										)
											? lineItem.description.substring(
													lineItem.description.split(/[0-9]/)[0].length,
													lineItem.description.length
											  )
											: lineItem.description}
									</BasicTextRight>
								</LineItemRows> */}
								<LineItemRows gap="sm">
									<BasicText variant="fineprintBold">Cost</BasicText>
									<BasicTextRight variant="fineprint">
										{`$${lineItem.price ? (lineItem.price / 100).toFixed(2) : 'N/A'}`}
									</BasicTextRight>
								</LineItemRows>
							</SpacedColumn>
						</LineItemCard>
					))}
				<BillTotalRow gap="md">
					<BasicText variant="fineprintBold">Total</BasicText>
					<BasicText variant="fineprint">{`$${total}`}</BasicText>
				</BillTotalRow>
				<DisclaimerCard bgColor="lightRed" radius="lg" borderless>
					<SpacedColumn gap="sm">
						<BasicText variant="fineprint">
							<ExclamationQuotesContainer>
								<ExclamationQuotes />
							</ExclamationQuotesContainer>
							{expiredBillDisclaimer}
						</BasicText>
					</SpacedColumn>
				</DisclaimerCard>
			</SpacedColumn>
		</>
	);
};

const BillDetails = ({
	bill,
	unpaidBillShipping,
	unpaidBillDisclaimer,
	expiredBillDisclaimer,
}: IMedicationBillCardProps) => {
	return (
		<InfoDisplayCard>
			<SpacedColumn gap="sm" marginBottom="sm">
				{bill.status === 'OPEN' && (
					<BillCardUnpaid
						bill={bill}
						unpaidBillShipping={unpaidBillShipping}
						unpaidBillDisclaimer={unpaidBillDisclaimer}
					/>
				)}
				{bill.status === 'UNCOLLECTIBLE' && (
					<BillCardExpired bill={bill} expiredBillDisclaimer={expiredBillDisclaimer} />
				)}
				{bill.status === 'PAID' && <NewBillCardPaid bill={bill} />}
			</SpacedColumn>
		</InfoDisplayCard>
	);
};

export default BillDetails;
